import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../services/apiError';
import {
  getAnalyticsSummary,
  getAnalyticsLog,
  getSuperAdminAnalyticsSummary,
  getSuperAdminAnalyticsLog,
  getSuperAdminDerasars,
} from '../../services/api';
import SummaryCards from './components/SummaryCards';
import TopTable from './components/TopTable';
import ActivityLogFilters from './components/ActivityLogFilters';
import ActivityLogTable from './components/ActivityLogTable';
import styles from './AnalyticsPage.module.css';

const PAGE_SIZE = 20;

const AnalyticsPage = () => {
  const { user, activeDerasarId } = useAuth();
  const isSuperAdmin = user?.role === 'platform_admin' || user?.role === 'super_admin';

  const [summary, setSummary] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState({ action: '', from: '', to: '', derasarId: '' });
  const [page, setPage] = useState(1);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(true);
  const [derasars, setDerasars] = useState([]);
  const [activeTab, setActiveTab] = useState('top');

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setSummaryLoading(true);
        const data = isSuperAdmin
          ? await getSuperAdminAnalyticsSummary()
          : await getAnalyticsSummary(activeDerasarId);
        setSummary(data);
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to load analytics summary'));
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [isSuperAdmin, activeDerasarId]);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        setLogLoading(true);
        const params = { page: 1, pageSize: 1000 };
        const data = isSuperAdmin
          ? await getSuperAdminAnalyticsLog(params)
          : await getAnalyticsLog(activeDerasarId, params);
        setAllItems(data.items || []);
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'Failed to load activity log'));
      } finally {
        setLogLoading(false);
      }
    };
    fetchLog();
  }, [isSuperAdmin, activeDerasarId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    getSuperAdminDerasars({ status: 'approved' })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.derasars || []);
        setDerasars(list.map((d) => ({ id: d.id, name: d.name })));
      })
      .catch(() => {});
  }, [isSuperAdmin]);

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      if (filters.action && item.action !== filters.action) return false;
      if (filters.from) {
        if (new Date(item.createdAt) < new Date(filters.from)) return false;
      }
      if (filters.to) {
        const toDate = new Date(filters.to);
        toDate.setHours(23, 59, 59, 999);
        if (new Date(item.createdAt) > toDate) return false;
      }
      if (isSuperAdmin && filters.derasarId) {
        if (String(item.derasarId) !== String(filters.derasarId)) return false;
      }
      return true;
    });
  }, [allItems, filters, isSuperAdmin]);

  const pagedItems = useMemo(
    () => filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredItems, page],
  );

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const topData = summary ? (isSuperAdmin ? summary.topDerasars : summary.topMembers) : [];
  const topTabLabel = isSuperAdmin ? 'Top Derasars This Month' : 'Top Members This Month';

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Analytics</h1>

      {summaryLoading ? (
        <div className={styles.loading}><div className="spinner" /></div>
      ) : summary && (
        <SummaryCards summary={summary} isSuperAdmin={isSuperAdmin} />
      )}

      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'top' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('top')}
        >
          {topTabLabel}
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'log' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('log')}
        >
          Activity Log
        </button>
      </div>

      {activeTab === 'top' && (
        <section className={styles.section}>
          <TopTable data={topData} isSuperAdmin={isSuperAdmin} />
        </section>
      )}

      {activeTab === 'log' && (
        <section className={styles.section}>
          <ActivityLogFilters
            filters={filters}
            onChange={handleFilterChange}
            isSuperAdmin={isSuperAdmin}
            derasars={derasars}
          />
          {logLoading ? (
            <div className={styles.loading}><div className="spinner" /></div>
          ) : (
            <ActivityLogTable
              items={pagedItems}
              total={filteredItems.length}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              isSuperAdmin={isSuperAdmin}
            />
          )}
        </section>
      )}
    </div>
  );
};

export default AnalyticsPage;
